import * as React from "react";
import { ConfirmationDialogResult, EnumHelper, GroupPermissionLevel } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import { SaveDataObj } from "../../../../framework/saveData";
import { IUnitPermissionUserGroup } from "../../../../models/userGroup/unitPermissionUserGroup";
import { Alert, Box, Checkbox, FormControlLabel, Grid, Radio, Stack } from "@mui/material";
import { IFolderNode } from "ui/Scripts/source/models/document/folderNode";
import { Dialog } from "source/components/framework/Dialog/Dialog/Dialog";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { PanelTitle } from "source/components/framework/Panel/PanelTitle";

//FolderNodePermissionDialog
export interface IFolderNodePermissionDialogProp {
    folderNode: IFolderNode;
    companyGroup: IUnitPermissionUserGroup;
    userGroup: IUnitPermissionUserGroup;
    onOk: (permission: number, clearSubFolderPermissions: boolean, copyPermissionToAllSameLevelFolders: boolean) => void;
    onRemove: () => void;
    onCancel: () => void;
}

export interface IFolderNodePermissionDialogState {
    permission: number;
    nonExplicitPermission: number;
    hasSiblings: boolean;
    clearSubFolderPermissions: boolean;
    copyPermissionToAllSameLevelFolders: boolean;
}

export class FolderNodePermissionDialog extends React.Component<IFolderNodePermissionDialogProp, IFolderNodePermissionDialogState> {
    private orgStateHash: string = "";

    constructor(props: IFolderNodePermissionDialogProp) {
        super(props);
        const nonExplicitGroupPermission = props.folderNode.getEffectiveNonExplicitPermission(props.companyGroup, props.userGroup);
        this.state = {
            permission: Math.min(props.userGroup ? props.folderNode.permission.permission : props.folderNode.permission.companyGroupPermission, Math.min(props.folderNode.maxPermissionLevel, GroupPermissionLevel.Write)),
            nonExplicitPermission: Math.min(props.userGroup ? nonExplicitGroupPermission.permission : nonExplicitGroupPermission.companyGroupPermission, GroupPermissionLevel.Write),
            hasSiblings: !props.folderNode.getIsRoot() && props.folderNode.hasSiblings(),
            clearSubFolderPermissions: false,
            copyPermissionToAllSameLevelFolders: false
        };
        const saveData = this.getSaveDataFromState(props, this.state);
        this.orgStateHash = saveData.hash;
    }

    handleSetPermission = (checked: boolean, permission: number) => {
        if (!checked) return;
        this.setState({
            permission
        });
    };

    handleSetClearSubFolderPermissions = (checked: boolean) => {
        this.setState({
            clearSubFolderPermissions: checked
        });
    };

    handleSetCopyPermissionToAllSameLevelFolders = (checked: boolean) => {
        this.setState({
            copyPermissionToAllSameLevelFolders: checked
        });
    };

    getSaveDataFromState = (props: IFolderNodePermissionDialogProp, state: IFolderNodePermissionDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("permission", state.permission);
        result.append("clearSubFolderPermissions", state.clearSubFolderPermissions);
        result.append("copyPermissionToAllSameLevelFolders", state.copyPermissionToAllSameLevelFolders);
        return result;
    };

    save = async () => {
        const state = this.state;
        this.props.onOk(
            state.permission,
            state.clearSubFolderPermissions,
            state.copyPermissionToAllSameLevelFolders
        );
    };

    handleSave = () => {
        this.save();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.save();
        } else {
            props.onCancel();
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        const nonePermission = EnumHelper.isEqual(state.permission, GroupPermissionLevel.None);
        const readPermission = !nonePermission && EnumHelper.isEqual(state.permission, GroupPermissionLevel.Read);
        const writePermission = !nonePermission && !readPermission && EnumHelper.isEqual(state.permission, GroupPermissionLevel.Write);
        return (
            <Dialog
                id="folderNodePermissionDialog"
                title={props.folderNode.name}
                body={
                    <Box sx={{ margin: "0 0 -1rem" }}>
                        <GridRow>
                            <Grid item sm={12}>
                                <PanelTitle title={Translations.Permission} />
                                <Stack>
                                    {EnumHelper.isGreaterOrEqualThan(props.folderNode.maxPermissionLevel, GroupPermissionLevel.None) &&
                                        <FormControlLabel
                                            label={Translations.AccessRightNone}
                                            control={
                                                <Radio
                                                    checked={nonePermission}
                                                    onChange={(_e, checked: boolean) => this.handleSetPermission(checked, GroupPermissionLevel.None)}
                                                />
                                            }
                                            sx={{ marginBottom: 0 }}
                                        />
                                    }
                                    {EnumHelper.isGreaterOrEqualThan(props.folderNode.maxPermissionLevel, GroupPermissionLevel.Read) &&
                                        <FormControlLabel
                                            label={Translations.AccessRightRead}
                                            control={
                                                <Radio
                                                    checked={readPermission}
                                                    onChange={(_e, checked: boolean) => this.handleSetPermission(checked, GroupPermissionLevel.Read)}
                                                />
                                            }
                                            sx={{ marginBottom: 0 }}
                                        />
                                    }
                                    {EnumHelper.isGreaterOrEqualThan(props.folderNode.maxPermissionLevel, GroupPermissionLevel.Write) &&
                                        <FormControlLabel
                                            label={Translations.AccessRightWrite}
                                            control={
                                                <Radio
                                                    checked={writePermission}
                                                    onChange={(_e, checked: boolean) => this.handleSetPermission(checked, GroupPermissionLevel.Write)}
                                                />
                                            }
                                            sx={{ marginBottom: 0 }}
                                        />
                                    }
                                </Stack>
                                <Alert
                                    severity="info"
                                    sx={{
                                        "marginTop": ".5rem",
                                        "whiteSpace": "pre-line",
                                        ".MuiAlert-message": {
                                            width: "100%"
                                        }
                                    }}>
                                    {nonePermission
                                        ? Translations.AccessRightNone_DocumentDescription
                                        : (readPermission
                                            ? Translations.AccessRightRead_DocumentDescription
                                            : (writePermission
                                                ? Translations.AccessRightWrite_DocumentDescription
                                                : ""))}
                                </Alert>
                            </Grid>
                            <Grid item sm={12}>
                                {(props.folderNode.hasChildren() || state.hasSiblings) &&
                                    <PanelTitle title={Translations.AdditionalSettings} />
                                }
                                {props.folderNode.hasChildren() &&
                                    <FormControlLabel
                                        label={Translations.ClearSubFolderPermissions}
                                        control={
                                            <Checkbox
                                                checked={state.clearSubFolderPermissions}
                                                onChange={(_e, v) => this.handleSetClearSubFolderPermissions(v)}
                                            />
                                        }
                                    />
                                }
                                {state.hasSiblings &&
                                    <FormControlLabel
                                        label={Translations.CopyPermissionToAllSameLevelFolders}
                                        control={
                                            <Checkbox
                                                checked={state.copyPermissionToAllSameLevelFolders}
                                                onChange={(_e, v) => this.handleSetCopyPermissionToAllSameLevelFolders(v)}
                                            />
                                        }
                                    />
                                }
                            </Grid>
                        </GridRow>
                    </Box>
                }
                buttons={[
                    { title: Translations.Remove, variant: "danger", onClick: props.folderNode.hasExplicitPermission(props.companyGroup?.code, props.userGroup?.code) ? props.onRemove : null },
                    { title: Translations.Close, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.OK, variant: "confirmation", onClick: this.handleSave },
                ]}
                onClose={this.handleCancel}/>
        );
    }
}